// Story 10.2 — TanStack mutation hook for the opt-in publish toggle.
//
// One signature drives BOTH endpoints:
//   POST /api/v1/vault/shares/:id/publish            { state: boolean }
//   POST /api/v1/vault/sealed-dossiers/:id/publish   { state: boolean }
//
// The `targetKind` parameter ("share" | "sealed_dossier") composes into
// the URL. Set-style body (idempotent under retry: same body = same
// result); the endpoint is per-DELTA on the audit side but returns 200 on
// a no-op, so a retried optimistic toggle is safe and needs no
// idempotency-key header.
//
// Unpublish takes effect on the NEXT request (no client cache is held past
// invalidation) — the AC's "immediate, ≤60s" requirement is met by
// invalidating the vault-items + share-detail caches onSettled.
//
// Consumers: the publish <Switch> on ShareDetailPage (shares) and on the
// VaultPage collection grid (both artifact kinds).

import { useMutation, useQueryClient } from "@tanstack/react-query";

import { attachRequestIdToScope } from "../../lib/api-client";
import type {
  PublishTargetKind,
  PublishToggleResponse,
  ShareDetailResponse,
} from "./vaultTypes";

export interface PublishToggleError {
  code: string;
  message: string;
  status: number;
}

export interface PublishToggleVariables {
  state: boolean;
}

interface UsePublishToggleArgs {
  targetKind: PublishTargetKind;
  targetId: string;
}

interface MutationContext {
  previousData: ShareDetailResponse | undefined;
}

function endpointFor(kind: PublishTargetKind, id: string): string {
  return kind === "share"
    ? `/api/v1/vault/shares/${id}/publish`
    : `/api/v1/vault/sealed-dossiers/${id}/publish`;
}

async function postPublish(
  targetKind: PublishTargetKind,
  targetId: string,
  state: boolean,
): Promise<PublishToggleResponse> {
  const response = await fetch(endpointFor(targetKind, targetId), {
    method: "POST",
    credentials: "include",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ state }),
  });
  attachRequestIdToScope(response);
  if (!response.ok) {
    let detail: PublishToggleError = {
      code: "PUBLISH_TOGGLE_FAILED",
      message: `Publish toggle failed (HTTP ${response.status})`,
      status: response.status,
    };
    try {
      const raw = await response.json();
      if (raw?.error) {
        detail = {
          code: raw.error.code,
          message: raw.error.message,
          status: response.status,
        };
      }
    } catch {
      // body not JSON
    }
    throw detail;
  }
  return (await response.json()) as PublishToggleResponse;
}

export function usePublishToggle({ targetKind, targetId }: UsePublishToggleArgs) {
  const queryClient = useQueryClient();
  return useMutation<
    PublishToggleResponse,
    PublishToggleError,
    PublishToggleVariables,
    MutationContext
  >({
    mutationFn: ({ state }) => postPublish(targetKind, targetId, state),
    onMutate: async ({ state }) => {
      // Optimistic update only applies to the share-detail cache (sealed
      // dossiers have no detail page). Cancel any in-flight refetch first.
      if (targetKind !== "share") {
        return { previousData: undefined };
      }
      await queryClient.cancelQueries({ queryKey: ["share-detail", targetId] });
      const previousData = queryClient.getQueryData<ShareDetailResponse>([
        "share-detail",
        targetId,
      ]);
      if (previousData) {
        const next: ShareDetailResponse = {
          data: {
            ...previousData.data,
            share: {
              ...previousData.data.share,
              is_public_in_collection: state,
            },
          },
        };
        queryClient.setQueryData(["share-detail", targetId], next);
      }
      return { previousData };
    },
    onError: (_err, _vars, context) => {
      if (context?.previousData) {
        queryClient.setQueryData(
          ["share-detail", targetId],
          context.previousData,
        );
      }
    },
    onSettled: () => {
      // Reconcile the grid + (for shares) the detail page against server
      // truth. Partial-key match on vault-items covers every cached
      // filter/sort variant.
      if (targetKind === "share") {
        queryClient.invalidateQueries({ queryKey: ["share-detail", targetId] });
      }
      queryClient.invalidateQueries({ queryKey: ["vault-items"] });
      // Epic 10 — a publish toggle from the detail page changes the showcase
      // pane's contents too.
      queryClient.invalidateQueries({ queryKey: ["showcase"] });
    },
  });
}
